import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAccountSignals, previewAccountScores } from '../api/client';
import { useScoringConfig } from '../scoring/ScoringConfigContext';
import { SIGNAL_FAMILIES } from '../scoring/scoringConfig';
import { familyLabel, familyClassName, formatDate, timeAgo, eventDate, isEventDateEstimated } from '../familyMeta';
import { sourceLabel } from '../sourceMeta';
import { X, ExternalLink } from 'lucide-react';

export default function AccountDrawer({ enabledSources }) {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const { config } = useScoringConfig();
  const [data, setData] = useState(null);
  const [scoreItem, setScoreItem] = useState(null);
  const [scoreLoaded, setScoreLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [scoreError, setScoreError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    getAccountSignals(accountId, {
      limit: 500,
      sources: enabledSources ? [...enabledSources] : undefined,
    })
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, enabledSources]);

  useEffect(() => {
    let cancelled = false;
    setScoreItem(null);
    setScoreLoaded(false);
    setScoreError(null);
    previewAccountScores(config, {
      accountIds: [accountId],
      sources: enabledSources ? [...enabledSources] : undefined,
      includeSignalBreakdown: true,
    })
      .then((response) => {
        if (!cancelled) {
          setScoreItem(response.items[0] || null);
          setScoreLoaded(true);
        }
      })
      .catch((e) => {
        if (!cancelled) setScoreError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, config, enabledSources]);

  function close() {
    navigate('/');
  }

  const scored = scoreItem
    ? {
        ...scoreItem,
        contrib: scoreItem.family_contributions,
        distinctFamilies: scoreItem.distinct_recent_families,
      }
    : null;

  return (
    <>
      <div className="overlay show" onClick={close} />
      <div className="drawer show">
        {(error || scoreError) && (
          <div className="drawer-body">
            <div className="state-msg error">
              {error ? `Couldn't load account: ${error}` : `Couldn't calculate score: ${scoreError}`}
            </div>
          </div>
        )}
        {!error && !scoreError && (!data || !scoreLoaded) && (
          <div className="drawer-body">
            <div className="state-msg">Loading…</div>
          </div>
        )}
        {!error && !scoreError && data && scoreLoaded && !scoreItem && (
          <div className="drawer-body">
            <button className="drawer-close" onClick={close} aria-label="Close"><X size={18} aria-hidden="true" /></button>
            <div className="state-msg">This account has no signals from the selected sources.</div>
          </div>
        )}
        {!error && !scoreError && data && scored && (
          <>
            <div className={`drawer-tier-banner tier-${scored.tier.toLowerCase()}`}>Tier {scored.tier}</div>
            <div className="drawer-head">
              <div>
                <div className="drawer-title">{data.account.name}</div>
                <div className="company-meta">
                  {[data.account.industry, data.account.country].filter(Boolean).join(' · ')}
                  {' · score '}
                  {scored.score.toFixed(0)}/100
                </div>
                {data.account.domain && (
                  <a
                    className="domain-link"
                    href={`https://${data.account.domain}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {data.account.domain} <ExternalLink size={13} aria-hidden="true" />
                  </a>
                )}
              </div>
              <button className="drawer-close" onClick={close} aria-label="Close"><X size={18} aria-hidden="true" /></button>
            </div>
            <div className="drawer-body">
              <h4>SCORE BREAKDOWN</h4>
              {SIGNAL_FAMILIES.map((family) => {
                const total = Object.values(scored.contrib).reduce((a, b) => a + b, 0) || 1;
                const pct = (scored.contrib[family] / total) * 100;
                return (
                  <div className="breakdown-row" key={family}>
                    <div className="cname">{familyLabel(family)}</div>
                    <div className="cbar">
                      <i style={{ width: `${pct}%` }} className={`fill-${family.toLowerCase()}`} />
                    </div>
                    <div className="cval">{scored.contrib[family].toFixed(1)}</div>
                  </div>
                );
              })}
              {scored.corroborated && (
                <div className="hint">
                  Corroborated across {scored.distinctFamilies} families — ×
                  {scored.corroboration_multiplier.toFixed(2)} bonus applied.
                </div>
              )}

              <h4>SIGNAL HISTORY ({data.total})</h4>
              <div className="timeline">
                {data.signals.map((sig) => (
                  <div className="tl-item" key={sig.id}>
                    <div className="tl-marker" />
                    <div className="tl-body">
                      <div className="tl-top">
                        <span>
                          {formatDate(eventDate(sig))} · {timeAgo(eventDate(sig))}
                          {isEventDateEstimated(sig) && (
                            <span title="No published date from the source — this is when we collected it, not necessarily when it happened."> (collected)</span>
                          )}
                        </span>
                        <span className={`badge ${familyClassName(sig.signal_family)}`}>
                          {familyLabel(sig.signal_family)}
                        </span>
                      </div>
                      <div className="tl-headline">{sig.signal_type.replace(/_/g, ' ')}</div>
                      <div className="tl-source">
                        Source: {sourceLabel(sig.source)}
                        {sig.evidence_url && (
                          <>
                            {' · '}
                            <a className="evidence-link" href={sig.evidence_url} target="_blank" rel="noreferrer">
                              View source <ExternalLink size={13} aria-hidden="true" />
                            </a>
                          </>
                        )}
                      </div>
                      {(() => {
                        const detail = scored.signal_breakdown.find((item) => item.signal_id === sig.id);
                        if (!detail) return null;
                        return (
                          <div className="signal-score-note">
                            Strength ×{detail.strength.toFixed(2)} · {detail.strength_reason}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ))}
                {data.signals.length === 0 && (
                  <div className="state-msg">No signals recorded for this account yet.</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
